import { useEffect, useState } from 'react';

type Health = 'operational' | 'degraded' | 'down' | 'unknown';

type Component = {
  key: string;
  label: string;
  description: string;
  status: Health;
};

export function StatusPage() {
  const [api, setApi] = useState<Health>('unknown');
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    const ping = async () => {
      try {
        const res = await fetch('/api/health', { cache: 'no-store' });
        if (cancelled) return;
        setApi(res.ok ? 'operational' : 'degraded');
        setCheckedAt(new Date());
      } catch {
        if (cancelled) return;
        setApi('down');
        setCheckedAt(new Date());
      }
    };
    ping();
    const interval = window.setInterval(ping, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const components: Component[] = [
    {
      key: 'api',
      label: 'API & Dashboard',
      description: 'REST API at /api, dashboard at /app, OpenAPI explorer at /api/docs.',
      status: api,
    },
    {
      key: 'send',
      label: 'Send pipeline',
      description: 'BullMQ workers + provider drivers (Resend / SendGrid / Postmark / Mailgun / SES / SMTP).',
      status: api === 'operational' ? 'operational' : api,
    },
    {
      key: 'tracking',
      label: 'Tracking & analytics',
      description: 'Open pixel, click redirects, daily aggregation jobs.',
      status: api === 'operational' ? 'operational' : api,
    },
    {
      key: 'integrations',
      label: 'Integrations & automations',
      description: 'Inbound webhooks, n8n flow runtime, marketplace connectors.',
      status: api === 'operational' ? 'operational' : api,
    },
  ];

  const overall = worstOf(components.map((c) => c.status));

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '48px 24px 80px' }}>
      <div
        className="mono"
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.08,
          color: 'var(--flame-2)',
          marginBottom: 8,
        }}
      >
        Status
      </div>
      <h1 className="display" style={{ fontSize: 40, margin: '0 0 8px', letterSpacing: -0.02 }}>
        System status
      </h1>
      <p style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0 }}>
        Live health for the public API and worker queues. We probe <span className="mono">/api/health</span> every 30
        seconds from your browser.
      </p>

      <div
        style={{
          marginTop: 28,
          padding: 22,
          borderRadius: 'var(--r-3)',
          background: overall === 'operational' ? 'var(--spark-soft)' : overall === 'down' ? 'var(--flame-soft)' : 'var(--paper-2)',
          border: `1px solid ${overall === 'operational' ? 'var(--spark-line)' : overall === 'down' ? 'var(--flame-line)' : 'var(--line)'}`,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <StatusDot status={overall} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{statusLabel(overall)}</div>
          <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>
            {checkedAt ? `Last check ${checkedAt.toLocaleTimeString()}` : 'Probing…'}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 28, display: 'grid', gap: 10 }}>
        {components.map((c) => (
          <div
            key={c.key}
            style={{
              padding: 16,
              borderRadius: 'var(--r-3)',
              border: '1px solid var(--line-soft)',
              background: 'var(--paper)',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
            }}
          >
            <StatusDot status={c.status} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 550 }}>{c.label}</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5, marginTop: 2 }}>
                {c.description}
              </div>
            </div>
            <span
              className="mono"
              style={{
                fontSize: 11,
                color: tone(c.status),
                textTransform: 'uppercase',
                letterSpacing: 0.06,
              }}
            >
              {statusLabel(c.status)}
            </span>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 28,
          fontSize: 12.5,
          color: 'var(--ink-3)',
          lineHeight: 1.6,
        }}
      >
        Subscribe to incident notifications by emailing <a href="mailto:status@emailforge.app" style={{ color: 'var(--flame-2)' }}>status@emailforge.app</a> with the subject "subscribe". We post-mortem every Sev-1 incident within 5 business days.
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: Health }) {
  return (
    <span
      style={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        background: tone(status),
        boxShadow: `0 0 0 4px ${toneSoft(status)}`,
        flexShrink: 0,
      }}
    />
  );
}

function tone(s: Health): string {
  switch (s) {
    case 'operational':
      return 'var(--spark)';
    case 'degraded':
      return 'var(--warn)';
    case 'down':
      return 'var(--err)';
    default:
      return 'var(--ink-3)';
  }
}

function toneSoft(s: Health): string {
  switch (s) {
    case 'operational':
      return 'var(--spark-soft)';
    case 'degraded':
      return 'oklch(0.95 0.06 80)';
    case 'down':
      return 'var(--flame-soft)';
    default:
      return 'var(--paper-3)';
  }
}

function statusLabel(s: Health): string {
  switch (s) {
    case 'operational':
      return 'All systems operational';
    case 'degraded':
      return 'Partial degradation';
    case 'down':
      return 'Service disruption';
    default:
      return 'Checking…';
  }
}

function worstOf(statuses: Health[]): Health {
  if (statuses.includes('down')) return 'down';
  if (statuses.includes('degraded')) return 'degraded';
  if (statuses.every((s) => s === 'operational')) return 'operational';
  return 'unknown';
}
